import { cn } from "@/utils";
import * as Dialog from "@radix-ui/react-dialog";
import { MdClose } from "react-icons/md";

interface Props {
  title?: string | React.ReactNode;
  detail?: string,
  description?: string | React.ReactNode;
  open?: boolean;
  className?: string;
  classNameContent?: string;
  classNameContentContainer?: string;
  children?: React.ReactNode;
  onOpenChange: (open: boolean) => void;
  classNameTitle?: string;
  hasCloseIcon?: boolean;
}

export function Modal(props: Props) {
  const {
    className,
    children,
    title,
    detail,
    description,
    open,
    onOpenChange,
    classNameContent,
    classNameContentContainer,
    classNameTitle,
    hasCloseIcon,
  } = props;

  // Handle modal clicks to prevent bubbling to parent elements
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    // Only close modal if clicking directly on overlay (not on content)
    if (e.target === e.currentTarget) {
      onOpenChange(false);
    }
    e.stopPropagation();
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-black/50 z-50"
          onClick={handleOverlayClick}
        >
          {hasCloseIcon && (
            <div className="relative">
              <MdClose
                className="absolute top-1 right-1 text-white hover:scale-105 cursor-pointer hover:opacity-80 w-6 h-6"
                onClick={handleOverlayClick}
              />
            </div>
          )}
        </Dialog.Overlay>
        <Dialog.Content
          className={cn(
            "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] lg:w-[42vw] border-0 outline-none modal-content",
            className,
          )}
          onClick={handleModalClick}
          onPointerDownOutside={(e) => e.preventDefault()}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
          }}
        >
          <div
            className={cn(
              "bg-white rounded-lg shadow-lg border-0 outline-none",
              classNameContentContainer,
            )}
          >
            {/* title and description */}
            <section
              className={cn(
                "bg-white w-full z-[51] px-4 rounded-t-lg border-b border-grey-300",
                classNameTitle,
              )}
            >
              <Dialog.Title>
                {title && (
                  <div className="space-y-2 py-4">
                    <p className="text-lg font-bold leading-7">{title}</p>
                    {detail && <p className="text-light_secondary font-normal text-xs leading-4.5">
                      {detail}
                    </p>}
                  </div>
                )}
              </Dialog.Title>
              {/* Add Description component - can be visually hidden if not needed */}
              <Dialog.Description
                className={
                  description ? "pb-2 text-sm text-gray-600" : "sr-only"
                }
              >
                {description || "Modal dialog"}
              </Dialog.Description>
            </section>
            {/* content */}
            <section
              className={cn(
                "max-h-[calc(100vh-8rem)] overflow-y-auto",
                classNameContent,
              )}
            >
              {children}
            </section>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
